import { useTranslation } from 'react-i18next'
import { Package, ArrowRight, Check, MapPin } from 'lucide-react'
import {
  type ConsumerClaimSummary,
  type WorkflowStateInfo,
} from '@/consumer/hooks/use-consumer-claims'
import { Badge } from '@/shared/components/ui/badge'
import { Image } from '@/shared/components/ui/image'
import { WorkflowStateBadge } from '@/shared/components/workflows'
import { cn } from '@/shared/lib/utils'

// ── Access badge ─────────────────────────────────────────

export function ClaimAccessBadge({
  accessType,
}: {
  accessType: ConsumerClaimSummary['access_type']
}) {
  const { t } = useTranslation()
  // No badge for claims the consumer owns (or a payload without the field).
  if (!accessType || accessType === 'owned') return null
  if (accessType === 'view_only') {
    // More prominent than "shared" - makes clear no action can be taken here.
    return <Badge variant="destructive">{t('consumerClaim.viewOnlyBadge', 'View only')}</Badge>
  }
  return <Badge variant="secondary">{t('consumerClaim.sharedBadge', 'Shared with you')}</Badge>
}

// ── Progress Stepper (compact, for cards) ──────────────
//
// WPS-138: prefer the claimant-facing stage tracker (the same data the claim
// detail page uses) when the tenant has stage config; otherwise fall back to
// the raw internal workflow states so tenants without cycle-time config keep
// the previous behaviour.

interface ProgressStep {
  key: string
  label: string
  isCurrent: boolean
  isCompleted: boolean
  isTerminalError: boolean
  isTerminalOk: boolean
}

function buildSteps(claim: ConsumerClaimSummary): ProgressStep[] {
  // Preferred: claimant stage tracker.
  if (claim.stages && claim.stages.length > 0) {
    const sorted = [...claim.stages].sort((a, b) => a.order - b.order)
    const currentKey = claim.current_stage?.key ?? null
    const currentIdx = sorted.findIndex((s) => s.key === currentKey)
    return sorted.map((stage, idx) => {
      const isCurrent = stage.key === currentKey
      // On a terminal claim the current (final) stage reads as completed.
      const isCompleted = currentIdx >= 0 ? idx < currentIdx : false
      return {
        key: stage.key,
        label: stage.label,
        isCurrent,
        isCompleted,
        // The compact card conveys pass/fail through the status badge (which
        // carries the state's own colour); the stage tracker just marks the
        // final stage as reached, without guessing an approve/deny outcome.
        isTerminalError: false,
        isTerminalOk: isCurrent && claim.is_terminal,
      }
    })
  }

  // Fallback: raw internal workflow states.
  const states: WorkflowStateInfo[] = claim.workflow_states
  const currentStateKey =
    claim.workflow_states.find((s) => s.label === claim.claim_state)?.key ?? null
  if (!states.length || !currentStateKey) return []
  const sorted = [...states].sort((a, b) => (a.is_initial ? -1 : b.is_initial ? 1 : 0))
  const currentIdx = sorted.findIndex((s) => s.key === currentStateKey)
  return sorted.map((state, idx) => {
    const isCurrent = state.key === currentStateKey
    return {
      key: state.key,
      label: state.label,
      isCurrent,
      isCompleted: idx < currentIdx,
      isTerminalError: isCurrent && state.is_terminal && state.key === 'rejected',
      isTerminalOk: isCurrent && state.is_terminal && state.key !== 'rejected',
    }
  })
}

function ClaimProgressBar({ claim }: { claim: ConsumerClaimSummary }) {
  const steps = buildSteps(claim)
  if (!steps.length) return null

  return (
    <div
      className="mt-3 -mx-1 overflow-x-auto rounded-[4px] px-1 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--green-border)]"
      tabIndex={0}
      role="region"
      aria-label="Claim progress"
    >
      <div className="flex w-max items-center gap-0">
        {steps.map((step, idx) => (
          <div key={step.key} className="flex items-center shrink-0">
            <div className="flex flex-col items-center gap-1">
              <div
                className={cn(
                  'flex items-center justify-center rounded-full transition-all',
                  step.isCurrent ? 'h-5 w-5 border-[1.5px]' : 'h-3.5 w-3.5 border-[1px]',
                  step.isCurrent
                    ? step.isTerminalError
                      ? 'border-[var(--status-error)] bg-[var(--status-error)]'
                      : step.isTerminalOk
                        ? 'border-[var(--status-ok)] bg-[var(--status-ok)]'
                        : 'border-[var(--green)] bg-[var(--green)]'
                    : step.isCompleted
                      ? 'border-transparent bg-[var(--green)]'
                      : 'border-[var(--rule)] bg-[var(--bg-subtle)]',
                )}
              >
                {step.isCompleted && <Check className="h-2 w-2 text-white" />}
                {step.isCurrent && <div className="h-1.5 w-1.5 rounded-full bg-white" />}
              </div>
              <span
                className={cn(
                  'whitespace-nowrap text-[8px] tracking-[0.04em]',
                  step.isCurrent
                    ? step.isTerminalError
                      ? 'font-medium text-[var(--status-error)]'
                      : step.isTerminalOk
                        ? 'font-medium text-[var(--status-ok)]'
                        : 'font-medium text-[var(--green)]'
                    : step.isCompleted
                      ? 'text-[var(--ink)]'
                      : 'text-[var(--ink-3)]',
                )}
              >
                {step.label}
              </span>
            </div>
            {idx < steps.length - 1 && (
              <div
                className={cn(
                  'mx-1 mb-3 h-[1.5px] w-5 rounded-[1px]',
                  step.isCompleted ? 'bg-[var(--green)]' : 'bg-[var(--rule)]',
                )}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Claim Card ─────────────────────────────────────────

export function ClaimCard({
  claim,
  onClick,
}: {
  claim: ConsumerClaimSummary
  onClick: () => void
}) {
  // WPS-138: show the claimant-facing status when available, matching the
  // claim detail page; fall back to the raw internal state label otherwise.
  const statusLabel = claim.external_status_label ?? claim.claim_state
  const statusStateKey =
    claim.workflow_states.find((s) => s.label === claim.claim_state)?.key ?? undefined
  // One consistent attention indicator (top-right corner) for both signals:
  // a pending action the customer must take, or an unread staff message.
  const needsAttention =
    claim.has_pending_consumer_action || claim.has_unread_notification

  return (
    <div
      className="group relative flex flex-col p-5 rounded-[8px] transition-all duration-150 cursor-pointer bg-[var(--bg-card)] border-[0.5px] border-solid border-[var(--rule)] hover:border-[var(--green-border)]"
      onClick={onClick}
    >
      {/* Attention indicator: pending action or unread claim message */}
      {needsAttention && (
        <div
          className="absolute -right-1.5 -top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-[#ef4444] shadow-[0_2px_6px_rgba(239,68,68,0.4)]"
          aria-label={
            claim.has_unread_notification
              ? 'Unread claim message'
              : 'Action needed'
          }
          title={
            claim.has_unread_notification
              ? 'Unread claim message'
              : 'Action needed'
          }
        >
          <div className="h-2.5 w-2.5 rounded-full bg-white" />
        </div>
      )}
      <div className="flex gap-5">
        {/* Product image */}
        <Image
          src={claim.product_image_url}
          alt={claim.product_name}
          className="h-20 w-20 flex-shrink-0 rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)]"
          fallback={<Package className="h-8 w-8 text-[var(--ink-3)]" />}
        />

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="mb-1 flex items-center gap-2">
            <p className="truncate text-[14px] leading-snug text-[var(--ink)]">
              {claim.product_name}
            </p>
            <ClaimAccessBadge accessType={claim.access_type} />
          </div>
          <p className="mb-1.5 truncate text-[11px] text-[var(--ink-3)]">
            {claim.claim_schema_name}
            {claim.host?.registration_number && (
              <> &middot; <span className="font-medium tracking-wide">{claim.host.registration_number}</span></>
            )}
            {claim.host?.make && (
              <> &middot; {claim.host.make}</>
            )}
          </p>
          {claim.host?.address?.display && (
            <p className="mb-1.5 flex items-center gap-1 text-[11px] text-[var(--ink-3)]">
              <MapPin className="h-3 w-3 shrink-0 opacity-70" aria-hidden="true" />
              <span className="truncate">{claim.host.address.display}</span>
            </p>
          )}

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {statusLabel && (
                <WorkflowStateBadge
                  stateKey={statusStateKey}
                  label={statusLabel}
                  color={claim.claim_state_color}
                  isTerminal={claim.is_terminal}
                />
              )}
            </div>
            <div className="flex items-center gap-1.5 text-[10px] text-[var(--ink-3)]">
              {claim.organisation_logo_url ? (
                <img
                  src={claim.organisation_logo_url}
                  alt={claim.organisation_name}
                  className="h-4 max-w-[80px] object-contain opacity-60"
                />
              ) : (
                <span>{claim.organisation_name}</span>
              )}
              <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </div>
        </div>
      </div>
      {/* Progress bar */}
      <ClaimProgressBar claim={claim} />
    </div>
  )
}
